import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import Navbar from "../../Navbar";
import { UrlApi } from "../../../services/apirest";
import { logEventos, verEnviarPDF } from "../../../services/services";
// Formularios de la V1, sin cambios
import DialogEntregado from "../../referencias-tablas/pedidos/DialogEntregado";
import FormBajaPedido2 from "../../referencias-tablas/pedidos/FormBajaPedido2";
import FormPending from "../../referencias-tablas/pedidos/FormPending";
import usePedidos from "./usePedidos";
import TablaPedidosV2 from "./TablaPedidosV2";
import ChipEstado from "./ChipEstado";
import DialogEditarPedido from "./DialogEditarPedido";
import DialogFacturar from "./DialogFacturar";
import DialogErrorFacturacion from "./DialogErrorFacturacion";
import DialogGestionesCC from "./DialogGestionesCC";
import {
  COLOR_MARCA,
  PESTANIAS,
  obtenerPermisos,
  pestaniaPorId,
  rangoPorDefecto,
  rangoValido,
} from "./constantes";

const CLAVE_PESTANIA = "pedidosV2.pestania";
const CLAVE_TAMANIO_PAGINA = "pedidosV2.tamanioPagina";

// Preferencias del usuario en el navegador (si el almacenamiento falla, se usan los valores por defecto)
const leerPreferencia = (clave) => {
  try {
    return localStorage.getItem(clave);
  } catch (e) {
    return null;
  }
};
const guardarPreferencia = (clave, valor) => {
  try {
    localStorage.setItem(clave, valor);
  } catch (e) {
    // sin preferencias guardadas
  }
};

const pestaniaHabilitada = (pestania, permisos) => !permisos.esComercial || !!pestania.comercial;

// Pedidos de la fuente de la pestaña, filtrados por sus estados
const pedidosDePestania = (pestania, datos) => {
  const lista = datos[pestania.fuente];
  if (!lista) return null;
  return pestania.estados ? lista.filter((p) => pestania.estados.includes(p.id_estado)) : lista;
};

function PedidosV2() {
  const permisos = useMemo(() => obtenerPermisos(), []);
  // Fecha de registro desde / hasta (inclusivas)
  const [rango, setRango] = useState(rangoPorDefecto);
  const rangoOk = rangoValido(rango);
  const { datos, cargando, error, actualizado, asegurarFuente, recargar } = usePedidos(rango);

  const [idPestania, setIdPestania] = useState(() => {
    // Comerciales: arrancan en "A facturar" (como en la V1)
    if (permisos.esComercial) return "aFacturar";
    const guardada = pestaniaPorId(leerPreferencia(CLAVE_PESTANIA));
    return pestaniaHabilitada(guardada, permisos) ? guardada.id : "todos";
  });
  const pestania = pestaniaPorId(idPestania);
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [paginacion, setPaginacion] = useState(() => ({
    pageIndex: 0,
    pageSize: parseInt(leerPreferencia(CLAVE_TAMANIO_PAGINA)) || 25,
  }));

  // Diálogo abierto: { tipo, pedido }
  const [dialogo, setDialogo] = useState(null);
  const [aviso, setAviso] = useState(null); // { severidad, mensaje }

  useEffect(() => {
    logEventos("acceso al menú Pedidos V2", "Granel", "Pedidos V2", localStorage.getItem("user"));
  }, []);

  // Facturados se carga recién al abrir su pestaña (o cuando el rango vuelve a ser válido)
  useEffect(() => {
    if (rangoOk) asegurarFuente(pestania.fuente);
  }, [pestania.fuente, asegurarFuente, rangoOk]);

  const cambiarFecha = (campo) => (e) => {
    setRango((r) => ({ ...r, [campo]: e.target.value }));
    setPaginacion((p) => ({ ...p, pageIndex: 0 }));
  };

  const cambiarPestania = (_, id) => {
    setIdPestania(id);
    setFiltroEstado(null);
    setPaginacion((p) => ({ ...p, pageIndex: 0 }));
    guardarPreferencia(CLAVE_PESTANIA, id);
  };

  const cambiarPaginacion = (actualizar) => {
    setPaginacion((anterior) => {
      const nueva = typeof actualizar === "function" ? actualizar(anterior) : actualizar;
      if (nueva.pageSize !== anterior.pageSize) guardarPreferencia(CLAVE_TAMANIO_PAGINA, String(nueva.pageSize));
      return nueva;
    });
  };

  const pedidosPestania = useMemo(() => pedidosDePestania(pestania, datos), [pestania, datos]);

  // Estados presentes en la pestaña, con su cantidad (chips de filtro / leyenda)
  const estadosPresentes = useMemo(() => {
    const cantidades = new Map();
    (pedidosPestania || []).forEach((p) => {
      const actual = cantidades.get(p.id_estado) || { idEstado: p.id_estado, valor: p.valor, cantidad: 0 };
      actual.cantidad += 1;
      cantidades.set(p.id_estado, actual);
    });
    return [...cantidades.values()].sort((a, b) => b.cantidad - a.cantidad);
  }, [pedidosPestania]);

  const pedidosVisibles = useMemo(() => {
    if (!pedidosPestania) return [];
    return filtroEstado === null ? pedidosPestania : pedidosPestania.filter((p) => p.id_estado === filtroEstado);
  }, [pedidosPestania, filtroEstado]);

  const filtrarEstado = (idEstado) => {
    setFiltroEstado((actual) => (actual === idEstado ? null : idEstado));
    setPaginacion((p) => ({ ...p, pageIndex: 0 }));
  };

  // Ver factura: igual que la V1, solo si hay un único comprobante para el pedido
  const verFactura = async (pedido) => {
    try {
      const respuesta = await fetch(`${UrlApi}/comprobantes/pedido/${pedido.id_pedido}`);
      if (!respuesta.ok) throw new Error(respuesta.statusText);
      const comprobantes = await respuesta.json();
      if (comprobantes.length === 1) {
        verEnviarPDF(comprobantes[0].id_factura, 0);
      } else if (!comprobantes.length) {
        setAviso({ severidad: "warning", mensaje: "No se encontró el comprobante de este pedido." });
      } else {
        setAviso({
          severidad: "error",
          mensaje: "Existe más de un comprobante para este pedido. Contacte a sistemas.",
        });
      }
    } catch (e) {
      console.error("Error al obtener la factura:", e);
      setAviso({ severidad: "error", mensaje: "No se pudo obtener la factura." });
    }
  };

  const onAccion = useCallback((accion, pedido) => {
    if (accion === "verFactura") verFactura(pedido);
    else setDialogo({ tipo: accion, pedido });
    // verFactura solo usa setters estables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cerrarDialogo = () => setDialogo(null);
  const cerrarYRecargar = () => {
    setDialogo(null);
    recargar();
  };

  // Cantidad para la etiqueta de la pestaña (null mientras no se cargó)
  const cantidadPestania = (p) => {
    const lista = pedidosDePestania(p, datos);
    return lista ? lista.length : null;
  };

  const cargandoPestania = !!cargando[pestania.fuente];

  return (
    <div>
      <Navbar />
      <Box sx={{ px: { xs: 1, md: 3 }, py: 2 }}>
        {/* Encabezado */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" fontWeight={700} color={COLOR_MARCA}>
            Pedidos
          </Typography>
          <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" spacing={1.5}>
            <TextField
              label="Registrado desde"
              type="date"
              size="small"
              value={rango.desde}
              onChange={cambiarFecha("desde")}
              error={!rangoOk}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170, bgcolor: "#fff" }}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              value={rango.hasta}
              onChange={cambiarFecha("hasta")}
              error={!rangoOk}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170, bgcolor: "#fff" }}
            />
            {actualizado ? (
              <Typography variant="caption" color="text.secondary">
                Actualizado {moment(actualizado).format("HH:mm")}
              </Typography>
            ) : null}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={recargar}
              disabled={!rangoOk || Object.values(cargando).some(Boolean)}
              sx={{ textTransform: "none" }}
            >
              Actualizar
            </Button>
          </Stack>
        </Stack>

        {!rangoOk ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Completá las dos fechas. La fecha desde no puede ser posterior a la fecha hasta.
          </Alert>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}

        {/* Pestañas con contador */}
        <Tabs
          value={idPestania}
          onChange={cambiarPestania}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
        >
          {PESTANIAS.map((p) => {
            const cantidad = cantidadPestania(p);
            return (
              <Tab
                key={p.id}
                value={p.id}
                disabled={!pestaniaHabilitada(p, permisos)}
                sx={{ textTransform: "none", fontWeight: 600 }}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{p.titulo}</span>
                    {cantidad !== null ? (
                      <Chip
                        size="small"
                        label={cantidad}
                        color={idPestania === p.id ? "primary" : "default"}
                        sx={{ height: 20, fontSize: 12 }}
                      />
                    ) : null}
                  </Stack>
                }
              />
            );
          })}
        </Tabs>

        {/* Estados de la pestaña: leyenda y filtro rápido */}
        {estadosPresentes.length > 1 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              FILTRAR POR ESTADO:
            </Typography>
            {estadosPresentes.map((e) => (
              <ChipEstado
                key={e.idEstado}
                idEstado={e.idEstado}
                valor={e.valor}
                cantidad={e.cantidad}
                seleccionado={filtroEstado === e.idEstado}
                onClick={() => filtrarEstado(e.idEstado)}
              />
            ))}
            {filtroEstado !== null ? (
              <Button size="small" onClick={() => filtrarEstado(filtroEstado)} sx={{ textTransform: "none" }}>
                Ver todos
              </Button>
            ) : null}
          </Stack>
        ) : null}

        <TablaPedidosV2
          pedidos={pedidosVisibles}
          pestania={pestania}
          cargando={cargandoPestania}
          permisos={permisos}
          onAccion={onAccion}
          paginacion={paginacion}
          onPaginacion={cambiarPaginacion}
        />
      </Box>

      {/* Diálogos */}
      {dialogo?.tipo === "editar" ? <DialogEditarPedido pedido={dialogo.pedido} onClose={cerrarYRecargar} /> : null}
      {dialogo?.tipo === "facturar" ? (
        <DialogFacturar
          pedido={dialogo.pedido}
          onClose={(huboEnvio) => (huboEnvio ? cerrarYRecargar() : cerrarDialogo())}
        />
      ) : null}
      {dialogo?.tipo === "verError" ? (
        <DialogErrorFacturacion
          pedido={dialogo.pedido}
          onClose={cerrarDialogo}
          onFacturar={permisos.esComercial ? null : () => setDialogo({ tipo: "facturar", pedido: dialogo.pedido })}
        />
      ) : null}
      {dialogo?.tipo === "gestionesCC" ? <DialogGestionesCC pedido={dialogo.pedido} onClose={cerrarDialogo} /> : null}
      {dialogo?.tipo === "visita" ? (
        <DialogEntregado
          pedido={dialogo.pedido}
          open
          setOpen={(abierto) => !abierto && cerrarDialogo()}
          actualizar={recargar}
        />
      ) : null}
      {dialogo?.tipo === "cancelar" ? (
        <FormBajaPedido2
          datos={dialogo.pedido}
          open
          setOpen={(abierto) => !abierto && cerrarDialogo()}
          actualizar={recargar}
        />
      ) : null}
      {dialogo?.tipo === "marcarAprobado" ? (
        <FormPending
          pedido={dialogo.pedido}
          open
          setOpen={(abierto) => !abierto && cerrarDialogo()}
          actualizar={recargar}
        />
      ) : null}

      <Snackbar
        open={!!aviso}
        autoHideDuration={6000}
        onClose={() => setAviso(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={aviso?.severidad || "info"} variant="filled" onClose={() => setAviso(null)}>
          {aviso?.mensaje}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default PedidosV2;
